import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { isKey, Key } from '@app/shared/key';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';

import { TagsSearchItem } from '@app/tags/tags';
import { UserInterest } from '@app/user/user-api.model';

import { OrgEndorsedService } from '@app/orgs/services/org-endorsed.service';
import { TranslateService } from '@ngx-translate/core';
import { TagsService } from '@app/tags/services/tags.service';
import { A11yService } from '@app/shared/services/a11y.service';
import { AuthService } from '@app/shared/services/auth.service';
import { PopoverService } from '@app/shared/services/popover.service';

/** A component that allows a user to search for and add a focus skill. */
@Component({
  selector: 'dgx-quick-add-focus-skill-popover',
  templateUrl: './quick-add-focus-skill-popover.component.html',
  styleUrls: ['./quick-add-focus-skill-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class QuickAddFocusSkillPopoverComponent implements OnInit {
  public static readonly searchDebounceTime: number = 300;
  @Input() public popoverTrigger: ElementRef;
  @Input() public isOpen: boolean;
  @Output()
  public isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  public skillSelected: EventEmitter<TagsSearchItem> = new EventEmitter<TagsSearchItem>();
  public searchTerm: string;
  public skills: any[];
  public emptySearch: boolean = false;
  public endorsedSrc: string;
  public isLoaded: boolean = false;
  public i18n = this.translateService.instant([
    'DashboardNav_SearchBySkill',
    'Core_Endorsed',
    'OrgSkills_EndorsedSkillTooltip',
    'OrgSkills_NoSkillsResultsFound',
  ]);
  @ViewChild('skillList') private skillList: ElementRef;
  private search = new Subject<string>();
  private cachedFirstSearch;

  constructor(
    private translateService: TranslateService,
    private authService: AuthService,
    private tagsService: TagsService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private a11yService: A11yService,
    private popoverService: PopoverService,
    private orgEndorsedService: OrgEndorsedService
  ) {}

  public ngOnInit(): void {
    this.search
      .pipe(
        debounceTime(QuickAddFocusSkillPopoverComponent.searchDebounceTime),
        switchMap((term) => {
          if (term === '') {
            if (this.cachedFirstSearch?.length) {
              return combineLatest([of(this.cachedFirstSearch), of(term)]);
            } else {
              return combineLatest([
                this.tagsService
                  .getUserTopTags(false, false)
                  .pipe(map((tags) => tags.filter((tag) => !tag.isFocused))),
                of(term),
              ]);
            }
          }
          return combineLatest([
            this.tagsService.suggestTag(term) as Observable<TagsSearchItem[]>,
            of(term),
          ]);
        })
      )
      .subscribe(([skills, term]) => {
        if (!skills.length && term) {
          // when there are no skills returned, allow the user to add the skill they typed in.
          this.emptySearch = true;
          this.skills = [{ title: this.searchTerm, name: this.searchTerm }]; // name and title are both used & needed
        } else {
          if (term === '') {
            this.cachedFirstSearch = skills;
          }
          this.emptySearch = false;
          this.skills = skills;
        }
        this.isLoaded = true;
        this.cdr.detectChanges();
      });

    this.endorsedSrc = this.orgEndorsedService.getEndorsedSrc(
      this.authService.authUser.defaultOrgInfo?.organizationId
    );
  }

  public onSearch(event: Event) {
    const term = (event.target as HTMLInputElement).value;
    this.search.next(term);
  }

  public onSelectItem(item) {
    if ('id' in item) {
      item.tagId = item.id; // the tag search item does not have TagId, so add it here.
    }
    this.skillSelected.emit(item);
    this.searchTerm = null;
    this.cachedFirstSearch = []; // bust cache after a user adds a new skill
  }

  public onPopoverOpenChange(isOpen: boolean) {
    this.isOpenChange.emit(isOpen);
    if (isOpen) {
      this.search.next('');
    } else {
      this.searchTerm = null;
    }
  }

  public onInputKeydown(e: KeyboardEvent) {
    if (isKey(e, Key.Tab)) {
      this.popoverService.close({ preventRefocus: true });
    }
    if (isKey(e, Key.Enter) && this.emptySearch && this.skills.length) {
      this.onSelectItem(this.skills[0]);
    }
    if (this.elementRef.nativeElement && isKey(e, Key.Down)) {
      e.preventDefault();
      this.a11yService.focusNextFocusable(this.skillList.nativeElement);
    }
  }

  public trackById(index: number, skill: TagsSearchItem): number {
    return skill.id;
  }
}
